<script setup lang="ts" generic="TData, TValue">
import { ColumnDef, SortingState, PaginationState, FlexRender, getCoreRowModel, getPaginationRowModel, getSortedRowModel, useVueTable } from '@tanstack/vue-table';
import { ArrowUp, ArrowDown, ChevronsUpDown, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-vue-next';
import { computed, watch, ref, useSlots, onMounted, onBeforeUnmount } from 'vue';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { TooltipProvider, Tooltip, TooltipTrigger, TooltipContent } from '@/components/ui/tooltip';
import FadeTransition from '@/components/transitions/FadeTransition.vue';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import Spinner from '@/components/ui/Spinner.vue';
import DataTableFacetFilter from './DataTableFacetFilter.vue';
import type { FacetOption, FacetSelection } from './types';

const props = withDefaults(
    defineProps<{
        columns: ColumnDef<TData, TValue>[];
        /*
         * Every row to display, already narrowed by whatever the parent filters on. This table
         * sorts and pages it client-side, and holds all of it, so a footer slot can total the whole
         * set rather than the page.
         */
        data: TData[];
        loading?: boolean;
        /**
         * Tailwind max-height class capping the scroll container, e.g. `max-h-[60vh]`. Set it to keep
         * the header - and the totals row under it - in place while the rows scroll past; leave it
         * off and the table grows to its full height with the page.
         */
        maxBodyHeight?: string;
        pageSize: number;
        pageSizeOptions: number[];
        page: number;
        enablePagination?: boolean;
        sorting?: SortingState;
        /*
         * Which facet values are ticked, by column id. The table renders and reports the selection
         * but does not act on it: the parent owns the vocabulary the options are built from, so it
         * owns applying them too, and `data` arrives already filtered.
         */
        columnFilters?: FacetSelection;
        /* Column id to the multiselect offered in that column header. */
        facets?: Record<string, { title: string; options: FacetOption[]; selectAll?: boolean }>;
        // eslint-disable-next-line
        rowId?: (row: TData) => string;
        // eslint-disable-next-line
        rowClass?: (row: TData) => string;
    }>(),
    {
        loading: false,
        maxBodyHeight: undefined,
        enablePagination: true,
        sorting: undefined,
        columnFilters: undefined,
        facets: undefined,
        rowId: undefined,
        rowClass: undefined,
    }
);

const emit = defineEmits<{
    'update:sorting': [value: SortingState];
    'update:columnFilters': [value: FacetSelection];
    'update:pageSize': [value: number];
    'update:page': [value: number];
    'row-click': [row: TData];
}>();

const slots = useSlots();

const internalSorting = ref<SortingState>([]);
const internalColumnFilters = ref<FacetSelection>({});

const pagination = computed<PaginationState>({
    get: () => ({
        pageSize: props.pageSize,
        pageIndex: props.page - 1,
    }),
    set: ({ pageSize, pageIndex }: PaginationState) => {
        emit('update:pageSize', pageSize);
        emit('update:page', pageIndex + 1);
    },
});
const sorting = computed({
    get: () => props.sorting ?? internalSorting.value,
    set: (value) => {
        if (props.sorting !== undefined) {
            emit('update:sorting', value);
        } else {
            internalSorting.value = value;
        }
    },
});
const columnFilters = computed({
    get: () => props.columnFilters ?? internalColumnFilters.value,
    set: (value) => {
        if (props.columnFilters !== undefined) {
            emit('update:columnFilters', value);
        } else {
            internalColumnFilters.value = value;
        }
    },
});

const columns = computed(() =>
    props.columns.map((column) => ({
        ...column,
        enableSorting: column.enableSorting ?? false,
    }))
);

const table = useVueTable({
    initialState: {
        sorting: sorting.value,
        pagination: pagination.value,
    },
    get data() {
        return props.data;
    },
    get columns() {
        return columns.value;
    },
    state: {
        get sorting() {
            return sorting.value;
        },
        get pagination() {
            return pagination.value;
        },
    },
    onSortingChange: (updater) => {
        sorting.value = typeof updater === 'function' ? updater(sorting.value) : updater;
    },
    onPaginationChange: (updater) => {
        pagination.value = typeof updater === 'function' ? updater(pagination.value) : updater;
    },
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getRowId: props.rowId,
    autoResetPageIndex: false,
});

/**
 * Classes pinning a header cell to the right edge of the table's own horizontal scroll area (see
 * `Table.vue`'s `overflow-x-auto` wrapper), for a column whose meta declares `sticky: 'right'` - the
 * shared records table's actions column, today. Combined with `maxBodyHeight`'s top-sticky header,
 * the cell becomes a pinned corner: `z-[25]` sits deliberately above this row's own `z-20` (so the
 * corner stays above the plain header cells scrolling out from under it, and above the `z-10`
 * body cells below) but below the full-table loading overlay's `z-30`, which must still cover it.
 */
function headerStickyClass(sticky: 'right' | undefined): string {
    if (sticky === 'right' && props.maxBodyHeight) {
        return 'sticky top-0 right-0 z-[25] bg-white border-l border-gray-200';
    }

    if (sticky === 'right') {
        return 'sticky right-0 z-20 bg-white border-l border-gray-200';
    }

    return props.maxBodyHeight ? 'sticky top-0 z-20 bg-white' : '';
}

/**
 * Classes pinning a totals cell to the right edge, mirroring `headerStickyClass` above and replacing
 * its own previous plain `maxBodyHeight`-only stickiness so the two never disagree on `z-index`. The
 * totals row's background (`bg-gray-50`, applied unconditionally by its caller) already occludes
 * whatever scrolls underneath, so this only ever adds position and the border.
 */
function totalsStickyClass(sticky: 'right' | undefined): string {
    const isRightSticky = sticky === 'right';

    if (!props.maxBodyHeight && !isRightSticky) {
        return '';
    }

    if (props.maxBodyHeight && isRightSticky) {
        return 'sticky right-0 z-[25] border-l border-gray-200';
    }

    if (props.maxBodyHeight) {
        return 'sticky z-20';
    }

    return 'sticky right-0 z-20 border-l border-gray-200';
}

/**
 * Classes pinning a body cell to the right edge, mirroring the helpers above. A body row is never
 * vertically sticky, so this only ever adds the horizontal half - at `z-10`, below the header/totals
 * stack so a scrolled-past row's actions cell never floats above the sticky rows pinned over it, but
 * above plain body cells so it still occludes them as they scroll underneath. `group-hover:bg-gray-50`
 * mirrors `TableRow`'s own hover background, which this cell's opaque `bg-white` would otherwise hide.
 */
function bodyCellStickyClass(sticky: 'right' | undefined): string {
    if (sticky !== 'right') {
        return '';
    }

    return 'sticky right-0 z-10 bg-white group-hover:bg-gray-50 border-l border-gray-200';
}

function getAlignClass(align: string | undefined): string {
    switch (align) {
        case 'right':
            return 'text-right';
        case 'center':
            return 'text-center';
        default:
            return 'text-left';
    }
}

const rows = computed(() => table.getRowModel().rows || []);
const rowsCount = computed(() => props.data.length);
const visibleLeafColumns = computed(() => table.getVisibleLeafColumns());
/** A totals row is rendered when any column offers one, directly under the column names. */
const hasTotals = computed(() => visibleLeafColumns.value.some((column) => slots[`totals[${column.id}]`] !== undefined));

const start = computed(() => pagination.value.pageIndex * pagination.value.pageSize + 1);
const end = computed(() => {
    const itemEnd = (pagination.value.pageIndex + 1) * pagination.value.pageSize;

    return rowsCount.value > itemEnd ? itemEnd : rowsCount.value;
});

function facetFor(columnId: string) {
    return props.facets?.[columnId];
}

function facetValue(columnId: string): (string | number)[] {
    return columnFilters.value[columnId] ?? [];
}

function setFacetValue(columnId: string, value: (string | number)[]) {
    const next = { ...columnFilters.value };

    if (value.length) {
        next[columnId] = value;
    } else {
        delete next[columnId];
    }

    columnFilters.value = next;
}

/*
 * Height of the header row, which the sticky totals row underneath pins itself to. Measured because
 * a header cell is `h-12` at its shortest but grows whenever a translated heading wraps, and a
 * fixed offset would then let the totals overlap the names they sit under.
 */
const root = ref<HTMLElement | null>(null);
const headerHeight = ref(0);
let headerResize: ResizeObserver | null = null;

onMounted(() => {
    const head = root.value?.querySelector('thead');

    if (!head) {
        return;
    }

    headerHeight.value = head.getBoundingClientRect().height;
    headerResize = new ResizeObserver(() => {
        headerHeight.value = head.getBoundingClientRect().height;
    });
    headerResize.observe(head);
});

onBeforeUnmount(() => {
    headerResize?.disconnect();
    headerResize = null;
});

watch([() => rows.value, () => columnFilters.value], () => {
    if (props.loading) {
        return;
    }

    const pageCount = table.getPageCount();

    // Filtering can shrink the set below the page the user is standing on.
    if (pagination.value.pageIndex > pageCount - 1) {
        table.setPageIndex(0);
    }
});
</script>

<template>
    <div ref="root" class="relative">
        <Table :container-class="maxBodyHeight ? ['overflow-y-auto', maxBodyHeight] : undefined">
            <TableHeader>
                <TableRow v-for="headerGroup in table.getHeaderGroups()" :key="headerGroup.id" class="hover:bg-inherit">
                    <TableHead
                        v-for="header in headerGroup.headers"
                        :key="header.id"
                        :class="[getAlignClass(header.column.columnDef.meta?.align), header.column.columnDef.meta?.headerClass || '', headerStickyClass(header.column.columnDef.meta?.sticky)]"
                    >
                        <slot :name="`header[${header.column.id}]`" :column="header.column">
                            <div
                                class="flex items-center gap-x-0.5"
                                :class="{ 'justify-end': header.column.columnDef.meta?.align === 'right', 'justify-center': header.column.columnDef.meta?.align === 'center' }"
                            >
                                <DropdownMenu v-if="header.column.getCanSort() && !header.isPlaceholder" :modal="false">
                                    <DropdownMenuTrigger as-child>
                                        <Button
                                            :aria-label="
                                                header.column.getIsSorted()
                                                    ? header.column.getIsSorted() === 'asc'
                                                        ? $t('global.uiElements.dataTable.buttons.sortedAscendingClickToSort')
                                                        : $t('global.uiElements.dataTable.buttons.sortedDescendingClickToSort')
                                                    : $t('global.uiElements.dataTable.buttons.clickToSort')
                                            "
                                            variant="ghost"
                                            class="h-auto px-3 -ml-3 gap-x-1.5 data-[state=open]:bg-gray-100"
                                        >
                                            <FlexRender :render="header.column.columnDef.header" :props="header.getContext()" />
                                            <FadeTransition mode="out-in">
                                                <ArrowUp v-if="header.column.getIsSorted() === 'asc'" class="h-4 w-4 -mr-0.5" aria-hidden="true" />
                                                <ArrowDown v-else-if="header.column.getIsSorted()" class="h-4 w-4 -mr-0.5" aria-hidden="true" />
                                                <ChevronsUpDown v-else class="h-4 w-4 transition-transform -mr-0.5" aria-hidden="true" />
                                            </FadeTransition>
                                        </Button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent align="start" class="space-y-0.5">
                                        <DropdownMenuItem
                                            :aria-label="$t('global.uiElements.dataTable.buttons.sortAscending')"
                                            :class="{ 'bg-gray-100': header.column.getIsSorted() === 'asc' }"
                                            @select="header.column.toggleSorting(false)"
                                        >
                                            <ArrowUp class="mr-2 h-4 w-4" />
                                            <span>{{ $t('global.uiElements.dataTable.buttons.ascending') }}</span>
                                        </DropdownMenuItem>
                                        <DropdownMenuItem
                                            :aria-label="$t('global.uiElements.dataTable.buttons.sortDescending')"
                                            :class="{ 'bg-gray-100': header.column.getIsSorted() === 'desc' }"
                                            @select="header.column.toggleSorting(true)"
                                        >
                                            <ArrowDown class="mr-2 h-4 w-4" />
                                            <span>{{ $t('global.uiElements.dataTable.buttons.descending') }}</span>
                                        </DropdownMenuItem>
                                    </DropdownMenuContent>
                                </DropdownMenu>
                                <FlexRender v-else-if="!header.isPlaceholder" :render="header.column.columnDef.header" :props="header.getContext()" />
                                <DataTableFacetFilter
                                    v-if="facetFor(header.column.id)"
                                    :title="facetFor(header.column.id)?.title ?? ''"
                                    :options="facetFor(header.column.id)?.options ?? []"
                                    :select-all="facetFor(header.column.id)?.selectAll"
                                    :model-value="facetValue(header.column.id)"
                                    @update:model-value="setFacetValue(header.column.id, $event)"
                                />
                            </div>
                        </slot>
                    </TableHead>
                </TableRow>
                <!-- The totals read as a second heading rather than a footer: the figures stay beside the
                     column names they belong to, and in a scrolling table they stay on screen. -->
            </TableHeader>
            <TableBody class="relative">
                <!-- Totals read as a second heading rather than a footer: the figures stay beside the
                     column names they belong to, and in a scrolling table they stay on screen. The
                     offset is measured, not assumed - a translated heading can wrap to two lines. -->
                <TableRow v-if="hasTotals" class="hover:bg-inherit">
                    <TableCell
                        v-for="column in visibleLeafColumns"
                        :key="column.id"
                        :class="[getAlignClass(column.columnDef.meta?.align), 'bg-gray-50 font-semibold text-gray-900', totalsStickyClass(column.columnDef.meta?.sticky)]"
                        :style="maxBodyHeight ? { top: `${headerHeight}px` } : undefined"
                    >
                        <slot :name="`totals[${column.id}]`" />
                    </TableCell>
                </TableRow>
                <template v-if="rows.length">
                    <TableRow v-for="row in rows" :key="row.id" :class="rowClass?.(row.original as TData)" @click="emit('row-click', row.original as TData)">
                        <TableCell
                            v-for="cell in row.getVisibleCells()"
                            :key="cell.id"
                            :class="[getAlignClass(cell.column.columnDef.meta?.align), cell.column.columnDef.meta?.cellClass || '', bodyCellStickyClass(cell.column.columnDef.meta?.sticky)]"
                        >
                            <slot :name="`item[${cell.column.id}]`" :item="row.original" :value="cell.getValue()">
                                <FlexRender :render="cell.column.columnDef.cell" :props="cell.getContext()" />
                            </slot>
                        </TableCell>
                    </TableRow>
                </template>
                <tr v-else>
                    <td :colspan="visibleLeafColumns.length" class="h-24 text-center text-gray-500">
                        {{ loading ? '' : $t('global.uiElements.dataTable.texts.noResults') }}
                    </td>
                </tr>
            </TableBody>
        </Table>
        <div v-if="enablePagination" class="flex w-full flex-col-reverse items-center justify-between gap-4 overflow-auto sm:flex-row sm:gap-8 px-4 py-3 border-t border-gray-200">
            <div class="flex-1 whitespace-nowrap text-normal text-gray-500">
                {{
                    $t('global.uiElements.dataTable.texts.showingRange')
                        .replace('%START%', rows.length ? start.toString() : '0')
                        .replace('%END%', end.toString())
                        .replace('%TOTAL%', rowsCount.toString())
                }}
            </div>
            <div class="flex items-center space-x-2">
                <div class="whitespace-nowrap text-normal font-medium">{{ $t('global.uiElements.dataTable.texts.pageSize') }}</div>
                <Select :model-value="String(pagination.pageSize)" @update:model-value="table.setPageSize(Number($event))">
                    <SelectTrigger class="w-24">
                        <SelectValue :placeholder="String(pagination.pageSize)" />
                        <template #icon>
                            <ChevronsUpDown class="size-4 opacity-50 flex-shrink-0" aria-hidden="true" />
                        </template>
                    </SelectTrigger>
                    <SelectContent side="top">
                        <SelectItem v-for="option in pageSizeOptions" :key="option" :value="String(option)" class="text-normal">{{ option }}</SelectItem>
                    </SelectContent>
                </Select>
            </div>
            <div class="flex items-center gap-x-4">
                <div class="flex items-center justify-center text-normal font-medium">
                    {{
                        $t('global.uiElements.dataTable.texts.page')
                            .replace('%CURRENT_PAGE%', (pagination.pageIndex + 1).toString())
                            .replace('%TOTAL%', (table.getPageCount() || 1).toString())
                    }}
                </div>
                <div class="flex items-center space-x-1">
                    <TooltipProvider>
                        <Tooltip>
                            <TooltipTrigger as-child>
                                <Button variant="outline" size="icon" :disabled="!table.getCanPreviousPage() || loading" @click="table.setPageIndex(0)">
                                    <span class="sr-only">{{ $t('global.uiElements.dataTable.buttons.firstPage') }}</span>
                                    <ChevronsLeft class="size-5" aria-hidden="true" />
                                </Button>
                            </TooltipTrigger>
                            <TooltipContent>
                                <p>{{ $t('global.uiElements.dataTable.buttons.firstPage') }}</p>
                            </TooltipContent>
                        </Tooltip>
                    </TooltipProvider>
                    <TooltipProvider>
                        <Tooltip>
                            <TooltipTrigger as-child>
                                <Button variant="outline" size="icon" :disabled="!table.getCanPreviousPage() || loading" @click="table.previousPage()">
                                    <span class="sr-only">{{ $t('global.uiElements.dataTable.buttons.previousPage') }}</span>
                                    <ChevronLeft class="size-5" aria-hidden="true" />
                                </Button>
                            </TooltipTrigger>
                            <TooltipContent>
                                <p>{{ $t('global.uiElements.dataTable.buttons.previousPage') }}</p>
                            </TooltipContent>
                        </Tooltip>
                    </TooltipProvider>
                    <TooltipProvider>
                        <Tooltip>
                            <TooltipTrigger as-child>
                                <Button variant="outline" size="icon" :disabled="!table.getCanNextPage() || loading" @click="table.nextPage()">
                                    <span class="sr-only">{{ $t('global.uiElements.dataTable.buttons.nextPage') }}</span>
                                    <ChevronRight class="size-5" aria-hidden="true" />
                                </Button>
                            </TooltipTrigger>
                            <TooltipContent>
                                <p>{{ $t('global.uiElements.dataTable.buttons.nextPage') }}</p>
                            </TooltipContent>
                        </Tooltip>
                    </TooltipProvider>
                    <TooltipProvider>
                        <Tooltip>
                            <TooltipTrigger as-child>
                                <Button variant="outline" size="icon" :disabled="!table.getCanNextPage() || loading" @click="table.setPageIndex(table.getPageCount() - 1)">
                                    <span class="sr-only">{{ $t('global.uiElements.dataTable.buttons.lastPage') }}</span>
                                    <ChevronsRight class="size-5" aria-hidden="true" />
                                </Button>
                            </TooltipTrigger>
                            <TooltipContent>
                                <p>{{ $t('global.uiElements.dataTable.buttons.lastPage') }}</p>
                            </TooltipContent>
                        </Tooltip>
                    </TooltipProvider>
                </div>
            </div>
        </div>
        <Transition
            enter-active-class="duration-200 ease-out"
            enter-from-class="opacity-0"
            enter-to-class="opacity-100"
            leave-active-class="duration-100 ease-in"
            leave-from-class="opacity-100"
            leave-to-class="opacity-0"
        >
            <div v-if="loading" class="absolute inset-0 z-30 flex items-center justify-center bg-white/80">
                <div class="flex justify-center items-center gap-2">
                    <div class="font-medium">{{ $t('global.uiElements.dataTable.texts.loading') }}</div>
                    <Spinner class="w-5 h-5 animate-spin" />
                </div>
            </div>
        </Transition>
    </div>
</template>
